import React, { useState } from "react";
import { FormErrors, FormProps, FormValues } from "../types";

const INITIAL_VALUES: FormValues = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  message: "",
  signed_up: false,
  accepted: false,
};

// const INITIAL_VALUES: FormValues = {
//   first_name: "ArcwareTest",
//   last_name: "Test21",
//   email: "m.spaskyi@arcware.com",
//   phone: "123123123123",
//   message: "lorem from",
//   signed_up: true,
//   accepted: true,
// };

export type UseFormProps = Pick<FormProps, "onSubmit">;

export const useForm = (props: UseFormProps) => {
  const { onSubmit } = props;

  const [values, setValues] = useState<FormValues>(INITIAL_VALUES);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormValues, boolean>>>({});

  const resetForm = () => {
    setValues(INITIAL_VALUES);
    setErrors({});
    setTouched({});
  };

  const validate = (fieldValues: Partial<FormValues> = values): FormErrors => {
    const temp: FormErrors = { ...errors };

    if ("first_name" in fieldValues) {
      temp.first_name = fieldValues.first_name ? "" : "Firstname is required";
    }

    if ("last_name" in fieldValues) {
      temp.last_name = fieldValues.last_name ? "" : "Lastname is required";
    }

    if ("email" in fieldValues) {
      if (!fieldValues.email) {
        temp.email = "Email is required";
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        temp.email = emailRegex.test(fieldValues.email) ? "" : "Enter a valid email";
      }
    }

    if ("phone" in fieldValues) {
      const phoneDigits = (fieldValues.phone ?? "").replace(/\D/g, "");
      temp.phone =
        phoneDigits.length && phoneDigits.length <= 7 ? "Enter a valid phone number" : "";
    }

    if ("accepted" in fieldValues) {
      temp.accepted = fieldValues.accepted ? "" : "You must accept the terms";
    }

    return temp;
  };

  const handleChange =
    (field: keyof FormValues) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value =
          ["signed_up", "accepted"].includes(field)
            ? (e as React.ChangeEvent<HTMLInputElement>).target.checked
            : e.target.value;

        const newValues = { ...values, [field]: value };
        setValues(newValues);

        const newErrors = validate({ [field]: value } as Partial<FormValues>);
        setErrors((prev) => ({ ...prev, ...newErrors }));
      };

  const handleBlur = (field: keyof FormValues) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const newErrors = validate({ [field]: values[field] } as Partial<FormValues>);
    setErrors((prev) => ({ ...prev, ...newErrors }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = validate(values);
    setErrors(newErrors);
    setTouched({
      first_name: true,
      last_name: true,
      email: true,
      phone: true,
      message: true,
      accepted: true,
    });

    const hasError = Object.values(newErrors).some((err) => err);
    if (hasError) return;

    try {
      await onSubmit?.(values);
      resetForm();
    } catch (err) {
      console.error("Submit failed:", err);
    }
  };

  const showError = (field: keyof FormValues): string => {
    if (!touched[field]) return "";
    return errors[field] ?? "";
  };

  return {
    values,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    showError,
    resetForm, // optional: expose manual reset too
  } as any;
};
